// ============================================
// ANIMATIONS - ウェーブとステージ開始のアニメーション
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getWaveStartTimer,
    setWaveStartTimer,
    setIsWaveStarting,
    getWave,
    getStageStartTimer
} from '../../store/index';
import { getGameAreaCenterX, easeOutCubic } from '../../utils/helpers';
import { HUD_COLORS } from '../../config/hud-colors';

// ============================================
// triggerWaveStartAnimation() - ウェーブ開始演出のトリガー
// ============================================
export function triggerWaveStartAnimation() {
    setWaveStartTimer(CONFIG.waveStart.duration);
    setIsWaveStarting(true);
}

// ============================================
// updateWaveStartAnimation() - ウェーブ開始演出の更新
// ============================================
export function updateWaveStartAnimation() {
    setWaveStartTimer(getWaveStartTimer() - 1);
    if (getWaveStartTimer() <= 0) {
        setIsWaveStarting(false);
    }
}

// ============================================
// WAVE HUD表示 レイアウト定数
// ============================================
const WAVE_HUD = {
    lineHalfWidth: 80,     // 水平アクセントラインの半幅
    bracketSize: 12,       // L字ブラケットの辺長
    bracketOffset: 40,     // ブラケットの初期オフセット（収束前）
    verticalSpan: 28,      // テキスト中心からアクセントラインまでの距離
};

// ============================================
// drawWaveStartAnimation() - ウェーブ開始演出の描画（HUDスタイル）
// ============================================
export function drawWaveStartAnimation() {
    const p = getP5();
    const progress = 1 - (getWaveStartTimer() / CONFIG.waveStart.duration);
    const cx = getGameAreaCenterX();
    const textY = CONFIG.height / 3;
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const m = HUD_COLORS.magenta;

    // === フェーズ別パラメータ ===
    // SCAN: 0.00-0.12
    const scanT = Math.min(1, progress / 0.12);
    // LOCK: 0.12-0.35
    const lockRaw = Math.max(0, Math.min(1, (progress - 0.12) / 0.23));
    const lockT = easeOutCubic(lockRaw);
    // FADE OUT: 0.70-1.00
    const fadeT = progress > 0.70 ? Math.min(1, (progress - 0.70) / 0.30) : 0;

    // マスターアルファ（フェードアウト適用）
    const masterAlpha = 1 - fadeT;
    if (masterAlpha <= 0) {
        return;
    }

    // テキスト出現アルファ（スキャン完了後にロックフェーズで出現）
    const textAlpha = lockT * masterAlpha;

    // ブラケット収束量（ロックフェーズで外→内）
    const bracketOff = WAVE_HUD.bracketOffset * (1 - lockT);
    // フェードアウト時にブラケットが再び開く
    const bracketFadeOff = WAVE_HUD.bracketOffset * 0.5 * fadeT;
    const totalBracketOff = bracketOff + bracketFadeOff;

    // アクセントライン幅（スキャンで左→右にスイープ）
    const lineWidth = WAVE_HUD.lineHalfWidth * scanT;

    ctx.save();

    // === 水平アクセントライン（上下） ===
    const lineAlpha = Math.min(scanT, masterAlpha);
    ctx.strokeStyle = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.7 * lineAlpha})`;
    ctx.lineWidth = 1;
    ctx.shadowColor = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.6 * lineAlpha})`;
    ctx.shadowBlur = 8;

    // 上ライン
    ctx.beginPath();
    ctx.moveTo(cx - lineWidth, textY - WAVE_HUD.verticalSpan);
    ctx.lineTo(cx + lineWidth, textY - WAVE_HUD.verticalSpan);
    ctx.stroke();

    // 下ライン
    ctx.beginPath();
    ctx.moveTo(cx - lineWidth, textY + WAVE_HUD.verticalSpan);
    ctx.lineTo(cx + lineWidth, textY + WAVE_HUD.verticalSpan);
    ctx.stroke();

    ctx.shadowBlur = 0;

    // === サイドブラケット（L字、左右） ===
    if (lockRaw > 0) {
        const bAlpha = lockT * masterAlpha;
        const bs = WAVE_HUD.bracketSize;
        const leftX = cx - WAVE_HUD.lineHalfWidth - totalBracketOff;
        const rightX = cx + WAVE_HUD.lineHalfWidth + totalBracketOff;
        const topY = textY - WAVE_HUD.verticalSpan;
        const botY = textY + WAVE_HUD.verticalSpan;

        ctx.strokeStyle = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.5 * bAlpha})`;
        ctx.lineWidth = 2;

        // 左上 L字
        ctx.beginPath();
        ctx.moveTo(leftX + bs, topY);
        ctx.lineTo(leftX, topY);
        ctx.lineTo(leftX, topY + bs);
        ctx.stroke();

        // 左下 L字
        ctx.beginPath();
        ctx.moveTo(leftX + bs, botY);
        ctx.lineTo(leftX, botY);
        ctx.lineTo(leftX, botY - bs);
        ctx.stroke();

        // 右上 L字
        ctx.beginPath();
        ctx.moveTo(rightX - bs, topY);
        ctx.lineTo(rightX, topY);
        ctx.lineTo(rightX, topY + bs);
        ctx.stroke();

        // 右下 L字
        ctx.beginPath();
        ctx.moveTo(rightX - bs, botY);
        ctx.lineTo(rightX, botY);
        ctx.lineTo(rightX, botY - bs);
        ctx.stroke();
    }

    // === "WAVE" ラベル（Orbitron、大きめ + グロー） ===
    if (textAlpha > 0) {
        const waveLabel = 'WAVE';
        const waveLabelSize = 20;
        const waveLabelY = textY - WAVE_HUD.verticalSpan - 14;

        ctx.font = `700 ${waveLabelSize}px Orbitron`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.9 * textAlpha})`;
        ctx.shadowColor = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.6 * textAlpha})`;
        ctx.shadowBlur = 10;
        ctx.fillText(waveLabel, cx, waveLabelY);
        ctx.shadowBlur = 0;

        // === ウェーブ番号（Orbitron、グラデーション + グロー） ===
        const textSize = CONFIG.waveStart.textSize;
        const scale = 1 + 0.3 * (1 - lockT); // 1.3→1.0
        const scaledSize = textSize * scale;
        const waveStr = getWave().toString();

        // ホールド中のパルスグロー
        let pulseGlow = 12;
        if (progress > 0.35 && progress < 0.70) {
            pulseGlow = 12 + 6 * Math.sin((progress - 0.35) / 0.35 * Math.PI * 3);
        }

        // グラデーションテキスト描画（Canvas 2D API）
        const gradient = ctx.createLinearGradient(cx, textY - scaledSize / 2, cx, textY + scaledSize / 2);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${textAlpha})`);
        gradient.addColorStop(1, `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${textAlpha})`);

        ctx.font = `700 ${scaledSize}px Orbitron`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = gradient;
        ctx.shadowColor = `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${0.5 * textAlpha})`;
        ctx.shadowBlur = pulseGlow;
        ctx.fillText(waveStr, cx, textY);
        ctx.shadowBlur = 0;
    }

    ctx.restore();

    // フォントを戻す
    p.textFont('Orbitron');
}

// ============================================
// drawStageStartAnimation() - ステージ開始演出の描画（自機登場）
// ============================================
export function drawStageStartAnimation() {
    const p = getP5();
    const progress = 1 - (getStageStartTimer() / CONFIG.stageStart.duration);

    p.textAlign(p.CENTER, p.CENTER);
    p.textFont('Orbitron');

    // 「WAVE 1」と「GET READY」テキスト
    const textY = p.height / 3;
    let readyAlpha = 255;

    if (progress < 0.3) {
        readyAlpha = progress / 0.3 * 255;
    } else if (progress > 0.7) {
        readyAlpha = (1 - progress) / 0.3 * 255;
    }

    // グロー効果
    p.fill(0, 255, 255, readyAlpha * 0.3);
    p.textSize(42);
    p.text('WAVE ' + getWave(), p.width / 2, textY);

    // メインテキスト
    p.fill(0, 255, 255, readyAlpha);
    p.textSize(40);
    p.text('WAVE ' + getWave(), p.width / 2, textY);

    // 「GET READY」テキスト
    if (progress > 0.2 && progress < 0.8) {
        let subAlpha = 255;
        if (progress < 0.4) {
            subAlpha = (progress - 0.2) / 0.2 * 255;
        } else if (progress > 0.6) {
            subAlpha = (0.8 - progress) / 0.2 * 255;
        }
        p.fill(255, 200, 0, subAlpha);
        p.textSize(20);
        p.text('GET READY', p.width / 2, textY + 45);
    }
}

